<div class="p-6 space-y-6 bg-base-100 min-h-screen">
  <!-- Breadcrumb -->
  <nav class="text-sm breadcrumbs opacity-60">
    <ul>
      <li><a [routerLink]="['/dashboard']">Dashboard</a></li>
      <li><a [routerLink]="['/products']">Products</a></li>
      <li class="font-semibold text-base-content">Categories</li>
    </ul>
  </nav>

  <!-- Header Section -->
  <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
    <div>
      <h1 class="text-3xl font-bold tracking-tight text-base-content">Categories</h1>
      <p class="text-base-content/60 mt-1">
        Product counts include nested categories and active/inactive products.
        Screen visibility controls only the customer Categories tab. Hiding a parent hides its branch there; products, search, Home and offers keep their existing setup.
      </p>
    </div>
    <div class="flex items-center gap-2">
      <button class="btn btn-outline gap-2" [class.btn-active]="reorderMode" (click)="toggleReorderMode()">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M8 9l4-4 4 4M16 15l-4 4-4-4" />
        </svg>
        {{ reorderMode ? 'Done Reordering' : 'Reorder Categories' }}
      </button>
      <button class="btn btn-ghost btn-sm" (click)="loadCategories()">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
      <button class="btn btn-primary gap-2 shadow-md hover:shadow-lg transition-all duration-300"
        (click)="createCategories()">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Add Category
      </button>
    </div>
  </div>

  @if (reorderMode) {
  <section class="card bg-base-100 border border-primary/20 shadow-md overflow-hidden">
    <div class="p-5 border-b border-base-200 bg-primary/5 flex flex-col xl:flex-row xl:items-end gap-4">
      <div class="flex-1">
        <h2 class="text-lg font-bold">Arrange category display order</h2>
        <p class="text-sm opacity-60 mt-1">
          Choose a level, then drag its categories into the order customers should see.
        </p>
      </div>
      <div class="form-control">
        <span class="label-text text-xs font-bold uppercase tracking-wide opacity-60 mb-2">Show status</span>
        <div class="join border border-base-300 bg-base-100">
          <button type="button" class="join-item btn btn-sm" [class.btn-active]="reorderStatusFilter === ''"
            [disabled]="savingOrder" (click)="setReorderStatus('')">All</button>
          <button type="button" class="join-item btn btn-sm"
            [class.btn-active]="reorderStatusFilter === 'true'"
            [disabled]="savingOrder" (click)="setReorderStatus('true')">Active</button>
          <button type="button" class="join-item btn btn-sm"
            [class.btn-active]="reorderStatusFilter === 'false'"
            [disabled]="savingOrder" (click)="setReorderStatus('false')">Inactive</button>
        </div>
      </div>
      <div class="form-control w-full lg:w-96">
        <label class="label py-1" for="reorderScope">
          <span class="label-text text-xs font-bold uppercase tracking-wide opacity-60">Categories under</span>
        </label>
        <select id="reorderScope" class="select select-bordered w-full bg-base-100"
          [(ngModel)]="reorderParentId" [disabled]="savingOrder">
          <option [ngValue]="null">Root categories (L0)</option>
          @for (scope of reorderScopes; track scope.id) {
          <option [ngValue]="scope.id">L{{ scope.level }} · {{ scope.label }}</option>
          }
        </select>
      </div>
    </div>

    <div class="p-5">
      @if (reorderLoading) {
      <div class="flex items-center justify-center gap-3 py-12 opacity-60">
        <span class="loading loading-spinner loading-md"></span>
        Loading category order...
      </div>
      } @else if (reorderVisibleItems.length === 0) {
      <div class="py-12 text-center opacity-50">
        No {{ reorderStatusFilter === 'true' ? 'active' : reorderStatusFilter === 'false' ? 'inactive' : '' }}
        categories exist directly under {{ reorderScopeLabel() }}.
      </div>
      } @else {
      <div cdkDropList [cdkDropListData]="reorderVisibleItems" [cdkDropListDisabled]="savingOrder"
        (cdkDropListDropped)="onCategoryDrop($event)" class="category-reorder-list space-y-2">
        @for (item of reorderVisibleItems; track item.id; let index = $index) {
        <div cdkDrag
          class="category-reorder-item flex items-center gap-4 p-3 bg-base-100 border border-base-300 rounded-xl shadow-sm">
          <button type="button" cdkDragHandle
            class="btn btn-ghost btn-sm btn-square cursor-grab active:cursor-grabbing"
            [disabled]="savingOrder" [attr.aria-label]="'Drag to reorder ' + item.name" title="Drag to reorder">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 opacity-50" viewBox="0 0 24 24"
              fill="currentColor" aria-hidden="true">
              <path d="M9 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-2 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM19 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-2 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm2 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" />
            </svg>
          </button>

          <div class="w-11 h-11 rounded-xl bg-base-200 border border-base-300 overflow-hidden flex items-center justify-center">
            @if (item.iconUrl) {
            <img [src]="item.iconUrl" [alt]="item.name + ' icon'" class="w-full h-full object-cover" />
            } @else {
            <span class="font-bold opacity-30">{{ item.name[0] }}</span>
            }
          </div>

          <div class="min-w-0 flex-1">
            <div class="font-bold truncate">{{ item.name }}</div>
            <div class="text-xs opacity-50">L{{ item.level }} · {{ item.active ? 'Active' : 'Inactive' }}</div>
          </div>

          <div class="badge badge-lg badge-neutral font-mono">#{{ index + 1 }}</div>
          <div *cdkDragPlaceholder class="category-reorder-placeholder"></div>
        </div>
        }
      </div>
      @if (savingOrder) {
      <div class="flex items-center gap-2 mt-4 text-sm text-primary">
        <span class="loading loading-spinner loading-sm"></span>
        Saving order...
      </div>
      }
      }
    </div>
  </section>
  }

  <!-- Search & Filter Toolbar -->
  <div class="flex flex-col sm:flex-row items-center gap-4 bg-base-200/50 p-4 rounded-xl border border-base-200">
    <div class="relative flex-1 w-full">
      <div class="absolute inset-y-0 left-3 flex items-center pointer-events-none opacity-50">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input type="text" placeholder="Search categories..." [(ngModel)]="searchText" (ngModelChange)="onFilterChange()"
        class="input input-bordered w-full pl-10 bg-base-100 focus:ring-2 focus:ring-primary/20 transition-all" />
    </div>

    <div class="tabs tabs-boxed bg-base-100 border border-base-300 p-1">
      <button class="tab tab-sm h-8 rounded-lg transition-all" [class.tab-active]="statusFilter === ''"
        (click)="setStatus('')">All</button>
      <button class="tab tab-sm h-8 rounded-lg transition-all" [class.tab-active]="statusFilter === 'true'"
        (click)="setStatus('true')">Active</button>
      <button class="tab tab-sm h-8 rounded-lg transition-all" [class.tab-active]="statusFilter === 'false'"
        (click)="setStatus('false')">Inactive</button>
    </div>
  </div>

  @if (countsError) {
    <p class="text-sm text-error" role="status">Product counts could not be loaded.
      <button type="button" class="underline" (click)="loadCategories()">Retry</button>
    </p>
  }
  <ng-template #productCount let-id>
    @if (productCounts[id]; as count) {
      <span class="badge badge-sm badge-outline mt-1" [attr.title]="count.direct_product_count + ' directly assigned; total includes nested categories'">
        {{ count.product_count | number }} products
      </span>
    } @else {
      <span class="text-xs opacity-50">{{ countsLoading ? 'Loading products...' : 'Count unavailable' }}</span>
    }
  </ng-template>
  <ng-template #screenVisibility let-cat>
    <label class="flex items-center gap-2 mt-3 text-xs font-medium">
      <input type="checkbox" class="toggle toggle-primary toggle-sm"
        [checked]="cat.showInCategoryScreen" [disabled]="visibilitySaving.has(cat.id)"
        [attr.aria-label]="'Show ' + cat.name + ' in customer Categories screen'"
        (change)="setCategoryScreenVisibility(cat, $event)">
      Show in customer Categories screen
    </label>
    @if (hiddenByParent(cat)) { <p class="text-xs text-warning mt-1">Hidden by parent category</p> }
    @else if (!cat.showInCategoryScreen) { <p class="text-xs opacity-60 mt-1">Hidden from Categories screen</p> }
    @else if (!cat.active) { <p class="text-xs opacity-60 mt-1">Shown when category is active</p> }
  </ng-template>

  <!-- Categories Table -->
  <div class="card bg-base-100 shadow-sm border border-base-200 overflow-hidden">
    <div class="overflow-x-auto">
      <table class="table table-md w-full border-separate border-spacing-0">
        <thead class="bg-base-200/50 text-base-content/70">
          <tr>
            <th class="w-12 rounded-tl-xl"></th>
            <th class="cursor-pointer group py-4" (click)="toggleSort('name')">
              <div class="flex items-center gap-2">
                Name
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-all"
                  [class.opacity-0]="sortBy !== 'name'" [class.rotate-180]="order === 'DESC'" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </div>
            </th>
            <th class="cursor-pointer text-center" (click)="toggleSort('position')">
              <div class="flex items-center justify-center gap-2">
                Position
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-all"
                  [class.opacity-0]="sortBy !== 'position'" [class.rotate-180]="order === 'DESC'" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </div>
            </th>
            <th class="text-center">Subcategories</th>
            <th>Status</th>
            <th class="text-right px-6 rounded-tr-xl">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-base-200">
          @if (loading) {
          @for (i of [1,2,3,4,5]; track i) {
          <tr class="animate-pulse">
            <td class="text-center">
              <div class="h-4 w-4 bg-base-300 rounded mx-auto"></div>
            </td>
            <td>
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 bg-base-300 rounded-xl"></div>
                <div class="space-y-2">
                  <div class="h-4 w-32 bg-base-300 rounded"></div>
                  <div class="h-3 w-16 bg-base-300 rounded"></div>
                </div>
              </div>
            </td>
            <td class="text-center">
              <div class="h-6 w-12 bg-base-300 rounded-full mx-auto"></div>
            </td>
            <td class="text-center">
              <div class="h-6 w-12 bg-base-300 rounded-full mx-auto"></div>
            </td>
            <td>
              <div class="h-8 w-24 bg-base-300 rounded-full"></div>
            </td>
            <td class="text-right px-6">
              <div class="h-8 w-20 bg-base-300 rounded mx-auto"></div>
            </td>
          </tr>
          }
          } @else if (categories.length === 0) {
          <tr>
            <td colspan="6" class="py-20 text-center">
              <div class="flex flex-col items-center gap-4 opacity-40">
                <div class="p-4 bg-base-200 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div class="space-y-1">
                  <p class="text-xl font-bold">No categories found</p>
                  <p class="text-sm">Try adjusting your search or filters</p>
                </div>
                <button class="btn btn-outline btn-sm" (click)="loadCategories()">Refresh List</button>
              </div>
            </td>
          </tr>
          } @else {
          @for (cat of categories; track cat.id) {
          <!-- Root Category -->
          <tr class="hover:bg-base-200/30 transition-colors group">
            <td class="text-center">
              <button
                class="btn btn-ghost btn-xs btn-circle transition-all duration-300 hover:bg-primary/10 hover:text-primary"
                [class.rotate-90]="isExpanded(cat.id)" (click)="toggleExpand(cat.id)">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </td>
            <td>
              <div class="flex items-center gap-4">
                <div class="avatar">
                  <div
                    class="w-12 h-12 rounded-xl bg-base-200 flex items-center justify-center overflow-hidden border border-base-300 shadow-sm group-hover:border-primary/30 transition-colors">
                    @if (cat.iconUrl) {
                    <img [src]="cat.iconUrl" alt="icon" class="object-cover" />
                    } @else {
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 opacity-20" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    }
                  </div>
                </div>
                <div>
                  <div class="font-bold text-lg text-base-content group-hover:text-primary transition-colors">
                    {{ cat.name }}</div>
                  <div class="text-[10px] opacity-40 uppercase tracking-widest font-bold">Root Level</div>
                  <ng-container [ngTemplateOutlet]="productCount" [ngTemplateOutletContext]="{ $implicit: cat.id }" />
                </div>
              </div>
            </td>
            <td class="text-center font-mono font-bold opacity-60">{{ cat.position }}</td>
            <td class="text-center">
              <div
                class="inline-flex items-center gap-1.5 px-3 py-1 bg-base-200 rounded-full text-xs font-bold opacity-70">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                {{ cat.products.length }} Subcategories
              </div>
            </td>
            <td>
              <button type="button"
                class="badge gap-2 py-3 px-4 border-none font-bold cursor-pointer transition-all hover:scale-105 active:scale-95 shadow-sm"
                [class.bg-success/10]="cat.active" [class.text-success]="cat.active" [class.bg-error/10]="!cat.active"
                [class.text-error]="!cat.active" (click)="toggleCategory(cat)" [attr.aria-pressed]="cat.active"
                [attr.aria-label]="cat.active ? 'Deactivate category' : 'Activate category'">

                <span class="w-2 h-2 rounded-full animate-pulse" [class.bg-success]="cat.active"
                  [class.bg-error]="!cat.active">
                </span>

                {{ cat.active ? 'Active' : 'Inactive' }}
              </button>
              <ng-container [ngTemplateOutlet]="screenVisibility" [ngTemplateOutletContext]="{ $implicit: cat }" />
            </td>
            <td class="text-right px-6">
              <div class="flex justify-end gap-2">
                <button class="btn btn-square btn-ghost btn-sm hover:bg-primary/10 hover:text-primary transition-all"
                  (click)="editCategory(cat)" title="Edit">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button class="btn btn-square btn-ghost btn-sm hover:bg-error/10 hover:text-error transition-all"
                  (click)="deleteCategory(cat)" title="Delete">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </td>
          </tr>

          <!-- Subcategories (Level 2) -->
          @if (isExpanded(cat.id)) {
          @for (sub of cat.products; track sub.id) {
          <tr class="bg-base-200/20 group/sub">
            <td class="text-center relative">
              <div class="absolute top-0 bottom-0 left-1/2 w-px bg-base-300 -translate-x-1/2 opacity-50"></div>
              @if (sub.products.length > 0) {
              <button
                class="btn btn-ghost btn-xs btn-circle relative z-10 bg-base-100 transition-all duration-300 hover:bg-primary/10 hover:text-primary"
                [class.rotate-90]="isExpanded(sub.id)" (click)="toggleExpand(sub.id)">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              } @else {
              <div class="w-2 h-2 rounded-full bg-base-300 mx-auto relative z-10"></div>
              }
            </td>
            <td class="pl-16 relative">
              <div class="absolute left-6 top-1/2 w-8 h-px bg-base-300 opacity-50"></div>
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-lg bg-base-100 flex items-center justify-center border border-base-300 shadow-sm group-hover/sub:border-primary/20 transition-colors overflow-hidden">
                  @if (sub.iconUrl) {
                  <img [src]="sub.iconUrl" alt="icon" class="w-full h-full object-cover" />
                  } @else {
                  <span class="text-xs opacity-30 font-bold uppercase">{{ sub.name[0] }}</span>
                  }
                </div>
                <div class="flex flex-col">
                  <span
                    class="font-semibold text-base-content/80 group-hover/sub:text-primary transition-colors">{{ sub.name }}</span>
                  <ng-container [ngTemplateOutlet]="productCount" [ngTemplateOutletContext]="{ $implicit: sub.id }" />
                  <span class="text-[10px] opacity-40 font-bold">L2 Subcategory</span>
                </div>
              </div>
            </td>
            <td class="text-center font-mono text-sm font-bold opacity-50">{{ sub.position }}</td>
            <td class="text-center">
              <div class="badge badge-sm badge-ghost font-bold opacity-60">{{ sub.products.length }} Sub</div>
            </td>
            <td>
              <button class="btn btn-xs btn-ghost gap-2 font-bold px-3 hover:bg-base-200"
                [class.text-success]="sub.active" [class.text-error]="!sub.active" (click)="toggleCategory(sub)">
                <span class="w-1.5 h-1.5 rounded-full" [class.bg-success]="sub.active"
                  [class.bg-error]="!sub.active"></span>
                {{ sub.active ? 'Active' : 'Inactive' }}
              </button>
              <ng-container [ngTemplateOutlet]="screenVisibility" [ngTemplateOutletContext]="{ $implicit: sub }" />
            </td>
            <td class="text-right px-6">
              <div class="flex justify-end gap-1 opacity-0 group-hover/sub:opacity-100 transition-opacity">
                <button class="btn btn-ghost btn-xs hover:text-primary" (click)="editCategory(sub)">Edit</button>
                <button class="btn btn-ghost btn-xs hover:text-error" (click)="deleteCategory(sub)">Delete</button>
              </div>
            </td>
          </tr>

          <!-- Level 3 -->
          @if (isExpanded(sub.id)) {
          @for (subsub of sub.products; track subsub.id) {
          <tr class="bg-base-300/10 group/subsub">
            <td class="relative">
              <div class="absolute top-0 bottom-0 left-1/2 w-px bg-base-300 -translate-x-1/2 opacity-30"></div>
            </td>
            <td class="pl-28 py-2 relative">
              <div class="absolute left-20 top-1/2 w-8 h-px bg-base-300 opacity-30"></div>
              <div
                class="flex items-center gap-3 text-sm text-base-content/60 group-hover/subsub:text-primary transition-colors">
                <div
                  class="w-9 h-9 rounded-lg bg-base-100 flex items-center justify-center border border-base-300 overflow-hidden">
                  @if (subsub.iconUrl) {
                  <img [src]="subsub.iconUrl" alt="icon" class="w-full h-full object-cover" />
                  } @else {
                  <span class="text-xs opacity-30 font-bold uppercase">{{ subsub.name[0] }}</span>
                  }
                </div>
                <div class="flex flex-col">
                  <span class="font-medium">{{ subsub.name }}</span>
                  <ng-container [ngTemplateOutlet]="productCount" [ngTemplateOutletContext]="{ $implicit: subsub.id }" />
                  <span class="text-[9px] opacity-40 font-bold uppercase tracking-tighter">L3 Subcategory</span>
                </div>
              </div>
            </td>
            <td class="text-center font-mono text-sm font-bold opacity-50">{{ subsub.position }}</td>
            <td class="text-center">—</td>
            <td>
              <button class="btn btn-xs btn-ghost gap-2 font-bold px-3 hover:bg-base-200"
                [class.text-success]="subsub.active" [class.text-error]="!subsub.active"
                (click)="toggleCategory(subsub)">
                <span class="w-1.5 h-1.5 rounded-full" [class.bg-success]="subsub.active"
                  [class.bg-error]="!subsub.active"></span>
                {{ subsub.active ? 'Active' : 'Inactive' }}
              </button>
              <ng-container [ngTemplateOutlet]="screenVisibility" [ngTemplateOutletContext]="{ $implicit: subsub }" />
            </td>
            <td class="text-right px-6">
              <div class="flex justify-end gap-1 opacity-0 group-hover/subsub:opacity-100 transition-opacity">
                <button class="btn btn-ghost btn-xs scale-90" (click)="editCategory(subsub)">Edit</button>
              </div>
            </td>
          </tr>
          }
          }
          }
          }
          }
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination Footer -->
    <div
      class="p-4 bg-base-200/30 border-t border-base-200 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div class="text-sm font-medium opacity-60">
        Showing <span class="text-base-content">{{ fromCategory }}</span> to
        <span class="text-base-content">{{ toCategory }}</span>
        of
        <span class="text-base-content">{{ total }}</span> root categories
      </div>
      <div class="join shadow-sm border border-base-300">
        <button class="join-item btn btn-sm bg-base-100 hover:bg-primary hover:text-primary-content transition-all"
          (click)="changePage(page - 1)" [disabled]="page === 1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button class="join-item btn btn-sm bg-base-100 no-animation font-bold px-6">Page {{ page }} of
          {{ totalPages }}</button>
        <button class="join-item btn btn-sm bg-base-100 hover:bg-primary hover:text-primary-content transition-all"
          (click)="changePage(page + 1)" [disabled]="page === totalPages">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</div>
